import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardCopy,
  HelpCircle,
  Loader2,
  Reply,
  Sparkles,
  TextCursorInput,
} from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button, Progress } from '../../../components/ui';
import { useNotification } from '../../../contexts/NotificationContext';
import {
  fetchSmartReplyTemplates,
  useAiAssistantEnabled,
} from '../../ai/useAiAssistant';
import type { AiSmartReplyTemplate } from '../../ai/types';

interface SmartReplyTemplatesPanelProps {
  emailId?: number;
  partitionAt?: string | null;
  clientId?: number;
  onUseTemplate?: (body: string) => void;
  onCreateReply?: (text: string) => void;
  layout?: 'chat' | 'standalone' | 'rail';
  compact?: boolean;
}

type TemplateAccent = {
  Icon: React.ComponentType<{ className?: string }>;
  chipActive: string;
  chipIdle: string;
  previewBorder: string;
  iconClass: string;
};

function sanitizeLabel(label: string): string {
  let s = label.trim();
  s = s.replace(/^short\s+label\s*[-–—:]\s*/i, '');
  s = s.replace(/^short\s+label\s+e\.?\s*g\.?\.?\s*/i, '');
  s = s.replace(/^label\s*[-–—:]\s*/i, '');
  s = s.replace(/\s+/g, ' ').trim();
  if (!s) return 'Reply';
  if (s.length > 4 && s === s.toUpperCase()) {
    s = s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return s;
}

function sanitizeBody(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) return trimmed;
  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    if (typeof parsed.body === 'string' && parsed.body.trim()) return parsed.body.trim();
    if (typeof parsed.message === 'string' && parsed.message.trim()) return parsed.message.trim();
    if (typeof parsed.text === 'string' && parsed.text.trim()) return parsed.text.trim();
    if (typeof parsed.content === 'string' && parsed.content.trim()) return parsed.content.trim();
    if (typeof parsed.reply === 'string' && parsed.reply.trim()) return parsed.reply.trim();
  } catch {
    /* keep original */
  }
  return trimmed;
}

function normalizeTemplates(templates: AiSmartReplyTemplate[]): AiSmartReplyTemplate[] {
  return templates.map((t) => ({
    ...t,
    label: sanitizeLabel(t.label),
    body: sanitizeBody(t.body),
  }));
}

function resolveTemplateAccent(label: string, tone?: string): TemplateAccent {
  const key = `${label} ${tone ?? ''}`.toLowerCase();
  if (key.includes('follow') || key.includes('question') || key.includes('clarif')) {
    return {
      Icon: HelpCircle,
      chipActive: 'bg-sky-600 text-white border-sky-600 shadow-sm',
      chipIdle: 'border-sky-500/35 bg-sky-500/8 text-sky-800 dark:text-sky-200 hover:bg-sky-500/15',
      previewBorder: 'border-sky-500/25',
      iconClass: 'text-sky-600 dark:text-sky-400',
    };
  }
  if (key.includes('next') || key.includes('step') || key.includes('action') || key.includes('propose')) {
    return {
      Icon: ArrowRight,
      chipActive: 'bg-violet-600 text-white border-violet-600 shadow-sm',
      chipIdle: 'border-violet-500/35 bg-violet-500/8 text-violet-800 dark:text-violet-200 hover:bg-violet-500/15',
      previewBorder: 'border-violet-500/25',
      iconClass: 'text-violet-600 dark:text-violet-400',
    };
  }
  if (key.includes('acknowledge') || key.includes('confirm') || key.includes('schedule') || key.includes('thanks')) {
    return {
      Icon: CheckCircle2,
      chipActive: 'bg-emerald-600 text-white border-emerald-600 shadow-sm',
      chipIdle: 'border-emerald-500/35 bg-emerald-500/8 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-500/15',
      previewBorder: 'border-emerald-500/25',
      iconClass: 'text-emerald-600 dark:text-emerald-400',
    };
  }
  return {
    Icon: Sparkles,
    chipActive: 'bg-indigo-600 text-white border-indigo-600 shadow-sm',
    chipIdle: 'border-slate-500/30 bg-slate-500/8 text-slate-700 dark:text-slate-200 hover:bg-slate-500/15',
    previewBorder: 'border-slate-500/20',
    iconClass: 'text-slate-600 dark:text-slate-400',
  };
}

const SMART_REPLY_PROGRESS_CAP = 94;

function SmartReplyProgress({ compact }: { compact?: boolean }) {
  const { colors } = useTheme();
  const [percent, setPercent] = useState(4);

  useEffect(() => {
    setPercent(4);
    const tick = window.setInterval(() => {
      setPercent((prev) => {
        if (prev >= SMART_REPLY_PROGRESS_CAP) return prev;
        const step = prev < 45 ? 1.4 : prev < 78 ? 0.7 : 0.25;
        return Math.min(SMART_REPLY_PROGRESS_CAP, prev + step);
      });
    }, 140);
    return () => window.clearInterval(tick);
  }, []);

  return (
    <div
      className={cn('rounded-lg border border-violet-500/25 bg-violet-500/[0.06] px-3 py-2', compact && 'px-2.5 py-1.5')}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <p className={cn('font-medium', compact ? 'text-[11px]' : 'text-xs', colors.textSecondary)}>
          Drafting reply options…
        </p>
        <span className={cn('shrink-0 tabular-nums text-[10px]', colors.textMuted)}>
          {Math.round(percent)}%
        </span>
      </div>
      <Progress value={percent} size="sm" className="h-1" />
    </div>
  );
}

interface TemplatePreviewProps {
  template: AiSmartReplyTemplate;
  accent: TemplateAccent;
  compact?: boolean;
  onCopy: () => void;
  onPrimary?: () => void;
  primaryLabel: string;
  copied: boolean;
}

const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  template,
  accent,
  compact,
  onCopy,
  onPrimary,
  primaryLabel,
  copied,
}) => {
  const { colors } = useTheme();
  const [showFull, setShowFull] = useState(false);
  const { Icon } = accent;
  const isLong = template.body.length > (compact ? 160 : 280) || template.body.split('\n').length > 4;

  useEffect(() => {
    setShowFull(false);
  }, [template.label, template.body]);

  return (
    <div className={cn('rounded-xl border bg-white/60 dark:bg-black/20', accent.previewBorder, colors.border)}>
      <div className={cn('flex items-start gap-2.5', compact ? 'p-3' : 'p-3.5')}>
        <span
          className={cn(
            'mt-0.5 flex shrink-0 items-center justify-center rounded-lg',
            compact ? 'h-7 w-7' : 'h-8 w-8',
            'bg-black/[0.04] dark:bg-white/[0.06]',
          )}
        >
          <Icon className={cn(compact ? 'h-3.5 w-3.5' : 'h-4 w-4', accent.iconClass)} aria-hidden />
        </span>
        <p
          className={cn(
            'min-w-0 flex-1 whitespace-pre-wrap leading-relaxed',
            compact ? 'text-xs' : 'text-sm',
            colors.text,
            !showFull && isLong && 'line-clamp-4',
          )}
        >
          {template.body}
        </p>
      </div>

      <div className={cn('flex flex-wrap items-center gap-2 border-t px-3 py-2', colors.border)}>
        {isLong ? (
          <button
            type="button"
            onClick={() => setShowFull((v) => !v)}
            className={cn(
              'inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium',
              colors.textMuted,
              colors.interactiveHover,
            )}
          >
            {showFull ? (
              <>
                <ChevronUp className="h-3 w-3" aria-hidden />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3" aria-hidden />
                Show more
              </>
            )}
          </button>
        ) : (
          <span className="min-w-[1rem] flex-1" />
        )}

        <div className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            onClick={onCopy}
            className={cn(
              'inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-[11px] font-medium',
              colors.border,
              colors.textSecondary,
              colors.interactiveHover,
            )}
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5" aria-hidden />
                Copied
              </>
            ) : (
              <>
                <ClipboardCopy className="h-3.5 w-3.5" aria-hidden />
                Copy
              </>
            )}
          </button>
          {onPrimary ? (
            <Button type="button" variant="default" size="sm" className="h-8 gap-1.5 text-xs" onClick={onPrimary}>
              {primaryLabel === 'Insert' ? (
                <TextCursorInput className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <Reply className="h-3.5 w-3.5" aria-hidden />
              )}
              {primaryLabel}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const SmartReplyTemplatesPanel: React.FC<SmartReplyTemplatesPanelProps> = ({
  emailId,
  partitionAt,
  clientId,
  onUseTemplate,
  onCreateReply,
  layout = 'chat',
  compact = false,
}) => {
  const isChat = layout === 'chat';
  const isCompact = compact || isChat;
  const { colors } = useTheme();
  const { showError, showSuccess } = useNotification();
  const enabled = useAiAssistantEnabled();
  const [templates, setTemplates] = useState<AiSmartReplyTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const primaryLabel = onUseTemplate ? 'Insert' : onCreateReply ? 'Use reply' : 'Copy';

  const generate = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchSmartReplyTemplates({ emailId, partitionAt, clientId });
      const normalized = normalizeTemplates(result);
      setTemplates(normalized);
      setSelectedIndex(0);
      if (normalized.length === 0) showError('No templates generated.');
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message || 'Could not generate reply templates');
    } finally {
      setLoading(false);
    }
  }, [emailId, partitionAt, clientId, showError]);

  const copyTemplate = useCallback(
    async (body: string, index: number) => {
      try {
        await navigator.clipboard.writeText(body);
        setCopiedIndex(index);
        showSuccess('Copied to clipboard');
        window.setTimeout(() => setCopiedIndex(null), 2000);
      } catch {
        showError('Could not copy');
      }
    },
    [showError, showSuccess],
  );

  const selected = templates[selectedIndex];
  const selectedAccent = useMemo(
    () => (selected ? resolveTemplateAccent(selected.label, selected.tone) : null),
    [selected],
  );

  const handlePrimary = useCallback(() => {
    if (!selected) return;
    if (onUseTemplate) onUseTemplate(selected.body);
    else if (onCreateReply) onCreateReply(selected.body);
    else void copyTemplate(selected.body, selectedIndex);
  }, [copyTemplate, onCreateReply, onUseTemplate, selected, selectedIndex]);

  const pickerBlock =
    templates.length > 0 && selected && selectedAccent ? (
      <div className={cn('space-y-3', loading && 'pointer-events-none opacity-50')}>
        <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Reply templates">
          {templates.map((t, index) => {
            const accent = resolveTemplateAccent(t.label, t.tone);
            const active = index === selectedIndex;
            return (
              <button
                key={`${t.label}-${index}`}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setSelectedIndex(index)}
                className={cn(
                  'shrink-0 rounded-full border px-3 py-1 text-[11px] font-medium transition-all',
                  active ? accent.chipActive : accent.chipIdle,
                )}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <TemplatePreview
          template={selected}
          accent={selectedAccent}
          compact={isCompact}
          copied={copiedIndex === selectedIndex}
          primaryLabel={primaryLabel}
          onCopy={() => void copyTemplate(selected.body, selectedIndex)}
          onPrimary={onUseTemplate || onCreateReply ? handlePrimary : undefined}
        />
      </div>
    ) : null;

  if (!enabled) {
    if (!isChat) return null;
    return (
      <div className="flex justify-start">
        <div
          className={cn(
            'max-w-[92%] rounded-2xl rounded-bl-md border px-4 py-3 text-xs',
            colors.border,
            colors.textMuted,
          )}
        >
          Smart replies require AI assistant (CRM & dashboard) in Settings.
        </div>
      </div>
    );
  }

  if (isChat) {
    return (
      <div className="flex justify-start">
        <div
          className={cn(
            'w-full max-w-[92%] overflow-hidden rounded-2xl rounded-bl-md border shadow-sm',
            'border-violet-500/30 bg-gradient-to-br from-violet-500/[0.09] via-indigo-500/[0.05] to-transparent',
            'dark:from-violet-500/15 dark:via-indigo-500/10',
            colors.border,
          )}
          aria-label="Smart replies"
        >
          <div className={cn('flex items-center gap-3 border-b px-4 py-3', colors.border)}>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 text-white shadow-md shadow-violet-500/20">
              <Sparkles className="h-4 w-4" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <p className={cn('text-sm font-semibold leading-tight', colors.text)}>Smart replies</p>
              <p className={cn('mt-0.5 text-xs', colors.textMuted)}>
                {templates.length
                  ? 'Choose a tone and use it as your reply'
                  : 'Three draft replies from this thread'}
              </p>
            </div>
            {templates.length > 0 ? (
              <Button
                variant="outline"
                size="sm"
                className="h-8 shrink-0 text-xs"
                onClick={() => void generate()}
                disabled={loading}
              >
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : 'Refresh'}
              </Button>
            ) : null}
          </div>

          <div className={cn('space-y-3', isCompact ? 'p-3' : 'p-4')}>
            {loading ? <SmartReplyProgress compact={isCompact} /> : null}

            {!loading && templates.length === 0 ? (
              <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
                <p className={cn('flex-1 text-xs leading-relaxed', colors.textMuted)}>
                  Generate ready-to-send options without starting a full chat turn.
                </p>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  className="shrink-0 gap-1.5"
                  onClick={() => void generate()}
                >
                  <Sparkles className="h-3.5 w-3.5" aria-hidden />
                  Suggest replies
                </Button>
              </div>
            ) : null}

            {pickerBlock}
          </div>
        </div>
      </div>
    );
  }

  return (
    <section
      className={cn('rounded-xl border overflow-hidden print:hidden', colors.border, colors.background)}
      aria-label="Smart replies"
    >
      <div
        className={cn(
          'flex items-center justify-between gap-2 border-b px-3 py-2.5',
          colors.border,
          colors.surfaceHover,
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <Sparkles className="h-4 w-4 shrink-0 text-violet-500" aria-hidden />
          <p className={cn('text-sm font-semibold', colors.text)}>Smart replies</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void generate()} disabled={loading}>
          {loading ? '…' : templates.length ? 'Refresh' : 'Suggest'}
        </Button>
      </div>
      <div className="space-y-3 p-3">
        {loading ? <SmartReplyProgress compact /> : null}
        {pickerBlock}
      </div>
    </section>
  );
};

export default SmartReplyTemplatesPanel;
